import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";

const planInfo: Record<string, { name: string; color: string; tokens: number; price: string }> = {
  free: { name: "Free", color: "#ffffff", tokens: 10, price: "무료" },
  fan: { name: "Fan", color: "#ff6eb4", tokens: 100, price: "₩9,900/월" },
  superfan: { name: "Super Fan", color: "#4fc3f7", tokens: 300, price: "₩24,900/월" },
};

const idolGroups = ["AURORA", "NEXUS", "PRISM", "기타"];

export default function SignupPage() {
  const [searchParams] = useSearchParams();
  const planParam = searchParams.get("plan") || "free";
  const selectedPlan = planInfo[planParam] || planInfo.free;

  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    email: "",
    password: "",
    passwordConfirm: "",
    nickname: "",
    favoriteGroup: "",
    agreeTerms: false,
    agreeMarketing: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [completed, setCompleted] = useState(false);

  const update = (key: string, value: string | boolean) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const validateStep1 = () => {
    const e: Record<string, string> = {};
    if (!form.email.includes("@")) e.email = "올바른 이메일을 입력하세요";
    if (form.password.length < 8) e.password = "비밀번호는 8자 이상이어야 합니다";
    if (form.password !== form.passwordConfirm) e.passwordConfirm = "비밀번호가 일치하지 않습니다";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep2 = () => {
    const e: Record<string, string> = {};
    if (!form.nickname.trim()) e.nickname = "닉네임을 입력하세요";
    if (!form.agreeTerms) e.agreeTerms = "이용약관에 동의해주세요";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) setCompleted(true);
  };

  if (completed) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white flex items-center justify-center px-6">
        <Navbar />
        <div className="text-center max-w-md mx-auto pt-20">
          <div
            className="w-24 h-24 flex items-center justify-center rounded-3xl mx-auto mb-6"
            style={{ background: `${selectedPlan.color}20`, border: `2px solid ${selectedPlan.color}50` }}
          >
            <i className="ri-check-double-line text-5xl" style={{ color: selectedPlan.color }} />
          </div>
          <h2 className="text-3xl font-black text-white mb-3">가입 완료!</h2>
          <p className="text-white/50 text-base mb-2">
            <span className="font-bold" style={{ color: selectedPlan.color }}>{selectedPlan.name} 플랜</span>으로 시작합니다
          </p>
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold mb-8"
            style={{ background: `${selectedPlan.color}15`, color: selectedPlan.color, border: `1px solid ${selectedPlan.color}30` }}
          >
            <i className="ri-coin-fill" />
            {selectedPlan.tokens} 토큰이 지급되었습니다!
          </div>
          <div className="flex flex-col gap-3">
            <Link
              to="/quests"
              className="w-full py-3.5 rounded-xl font-bold text-white text-center cursor-pointer whitespace-nowrap"
              style={{ background: `linear-gradient(135deg, #ff6eb4, #4fc3f7)` }}
            >
              <i className="ri-film-line mr-2" />
              캠페인 참여하기
            </Link>
            <Link
              to="/"
              className="w-full py-3.5 rounded-xl font-bold text-white/60 text-center border border-white/15 hover:border-white/30 hover:text-white transition-all cursor-pointer whitespace-nowrap"
            >
              홈으로 이동
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <Navbar />

      <div className="max-w-5xl mx-auto px-6 pt-28 pb-20">
        <div className="flex flex-col lg:flex-row gap-10 items-start">

          {/* Left - Form */}
          <div className="flex-1 w-full">
            {/* Step Indicator */}
            <div className="flex items-center gap-3 mb-8">
              {[1, 2].map((s) => (
                <div key={s} className="flex items-center gap-3">
                  <div
                    className="w-8 h-8 flex items-center justify-center rounded-full text-sm font-bold transition-all"
                    style={
                      step > s
                        ? { background: "#ff6eb4", color: "#0a0a0f" }
                        : step === s
                        ? { background: "rgba(255,110,180,0.2)", color: "#ff6eb4", border: "1px solid #ff6eb4" }
                        : { background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.3)", border: "1px solid rgba(255,255,255,0.1)" }
                    }
                  >
                    {step > s ? <i className="ri-check-line text-xs" /> : s}
                  </div>
                  <span className={`text-sm font-medium ${step === s ? "text-white" : "text-white/30"}`}>
                    {s === 1 ? "계정 정보" : "프로필 설정"}
                  </span>
                  {s < 2 && <div className="w-8 h-px bg-white/10" />}
                </div>
              ))}
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/5 p-8">
              {step === 1 && (
                <>
                  <h2 className="text-xl font-bold text-white mb-6">계정 정보 입력</h2>
                  <div className="flex flex-col gap-5">
                    <div>
                      <label className="text-white/60 text-xs font-semibold mb-2 block">이메일 *</label>
                      <input
                        type="email"
                        value={form.email}
                        onChange={(e) => update("email", e.target.value)}
                        placeholder="example@email.com"
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/20 outline-none focus:border-[#ff6eb4]/50 transition-colors"
                      />
                      {errors.email && <p className="text-[#ff4757] text-xs mt-1">{errors.email}</p>}
                    </div>
                    <div>
                      <label className="text-white/60 text-xs font-semibold mb-2 block">비밀번호 *</label>
                      <input
                        type="password"
                        value={form.password}
                        onChange={(e) => update("password", e.target.value)}
                        placeholder="8자 이상 입력"
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/20 outline-none focus:border-[#ff6eb4]/50 transition-colors"
                      />
                      {errors.password && <p className="text-[#ff4757] text-xs mt-1">{errors.password}</p>}
                    </div>
                    <div>
                      <label className="text-white/60 text-xs font-semibold mb-2 block">비밀번호 확인 *</label>
                      <input
                        type="password"
                        value={form.passwordConfirm}
                        onChange={(e) => update("passwordConfirm", e.target.value)}
                        placeholder="비밀번호 재입력"
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/20 outline-none focus:border-[#ff6eb4]/50 transition-colors"
                      />
                      {errors.passwordConfirm && <p className="text-[#ff4757] text-xs mt-1">{errors.passwordConfirm}</p>}
                    </div>
                  </div>
                </>
              )}

              {step === 2 && (
                <>
                  <h2 className="text-xl font-bold text-white mb-6">팬 프로필 설정</h2>
                  <div className="flex flex-col gap-5">
                    <div>
                      <label className="text-white/60 text-xs font-semibold mb-2 block">닉네임 *</label>
                      <input
                        type="text"
                        value={form.nickname}
                        onChange={(e) => update("nickname", e.target.value)}
                        placeholder="커뮤니티에서 사용할 닉네임"
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/20 outline-none focus:border-[#ff6eb4]/50 transition-colors"
                        maxLength={20}
                      />
                      {errors.nickname && <p className="text-[#ff4757] text-xs mt-1">{errors.nickname}</p>}
                    </div>
                    <div>
                      <label className="text-white/60 text-xs font-semibold mb-2 block">최애 그룹 (선택)</label>
                      <div className="flex flex-wrap gap-2">
                        {idolGroups.map((g) => (
                          <button
                            key={g}
                            onClick={() => update("favoriteGroup", g)}
                            className={`px-4 py-2 rounded-full border text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                              form.favoriteGroup === g
                                ? "border-[#ff6eb4]/60 bg-[#ff6eb4]/15 text-[#ff6eb4]"
                                : "border-white/10 text-white/50 hover:border-white/20"
                            }`}
                          >
                            {g}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="flex flex-col gap-3">
                      <label
                        className="flex items-start gap-3 cursor-pointer"
                        onClick={() => update("agreeTerms", !form.agreeTerms)}
                      >
                        <div className={`w-5 h-5 flex items-center justify-center rounded border flex-shrink-0 mt-0.5 transition-all ${form.agreeTerms ? "bg-[#ff6eb4] border-[#ff6eb4]" : "border-white/20"}`}>
                          {form.agreeTerms && <i className="ri-check-line text-white text-xs" />}
                        </div>
                        <span className="text-white/60 text-sm">
                          <a href="#" className="text-[#ff6eb4] hover:underline">이용약관</a> 및{" "}
                          <a href="#" className="text-[#ff6eb4] hover:underline">개인정보처리방침</a>에 동의합니다 *
                        </span>
                      </label>
                      {errors.agreeTerms && <p className="text-[#ff4757] text-xs">{errors.agreeTerms}</p>}
                      <label
                        className="flex items-start gap-3 cursor-pointer"
                        onClick={() => update("agreeMarketing", !form.agreeMarketing)}
                      >
                        <div className={`w-5 h-5 flex items-center justify-center rounded border flex-shrink-0 mt-0.5 transition-all ${form.agreeMarketing ? "bg-[#ff6eb4] border-[#ff6eb4]" : "border-white/20"}`}>
                          {form.agreeMarketing && <i className="ri-check-line text-white text-xs" />}
                        </div>
                        <span className="text-white/60 text-sm">마케팅 정보 수신에 동의합니다 (선택)</span>
                      </label>
                    </div>
                  </div>
                </>
              )}

              <button
                onClick={handleNext}
                className="w-full mt-6 py-3.5 rounded-xl font-bold text-sm text-white cursor-pointer whitespace-nowrap hover:opacity-90 transition-opacity"
                style={{ background: "linear-gradient(135deg, #ff6eb4, #4fc3f7)" }}
              >
                {step === 1 ? "다음 단계" : "가입 완료"}
                <i className="ri-arrow-right-line ml-2" />
              </button>

              <p className="text-center text-white/30 text-xs mt-4">
                이미 계정이 있으신가요?{" "}
                <Link to="/" className="text-[#ff6eb4] hover:underline cursor-pointer">로그인</Link>
              </p>
            </div>
          </div>

          {/* Right - Plan Summary */}
          <div className="w-full lg:w-72 flex-shrink-0">
            <div
              className="rounded-2xl border p-6 sticky top-24"
              style={{ borderColor: `${selectedPlan.color}30`, background: `${selectedPlan.color}08` }}
            >
              <h3 className="text-white/60 text-xs font-semibold uppercase tracking-wider mb-4">선택한 플랜</h3>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl" style={{ background: `${selectedPlan.color}20` }}>
                  <i className="ri-vip-crown-fill text-lg" style={{ color: selectedPlan.color }} />
                </div>
                <div>
                  <div className="text-white font-black text-lg">{selectedPlan.name}</div>
                  <div className="font-bold text-sm" style={{ color: selectedPlan.color }}>{selectedPlan.price}</div>
                </div>
              </div>

              <div className="rounded-xl p-4 mb-5" style={{ background: `${selectedPlan.color}12`, border: `1px solid ${selectedPlan.color}25` }}>
                <div className="flex items-center gap-2 mb-1">
                  <i className="ri-coin-fill" style={{ color: selectedPlan.color }} />
                  <span className="font-black text-xl" style={{ color: selectedPlan.color }}>{selectedPlan.tokens}</span>
                  <span className="text-white/50 text-sm">토큰/월</span>
                </div>
                <p className="text-white/40 text-xs">AI 영상 생성에 사용</p>
              </div>

              <div className="flex flex-col gap-2 mb-5">
                {[
                  `약 ${selectedPlan.tokens}회 AI 영상 생성`,
                  "뮤직비디오 신 제작",
                  "안무 영상 제작",
                  "커뮤니티 공유",
                ].map((f) => (
                  <div key={f} className="flex items-center gap-2 text-white/50 text-xs">
                    <i className="ri-check-line text-xs" style={{ color: selectedPlan.color }} />
                    {f}
                  </div>
                ))}
              </div>

              <Link
                to="/pricing"
                className="flex items-center justify-center gap-1 text-white/30 text-xs hover:text-white/60 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line text-xs" />
                플랜 변경하기
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
